import { useState } from 'react'
import { GlobalInboundRollbackRequest } from '@/console/api/types'
import { Button } from '@/console/ui/Button'
import { Callout } from '@/console/ui/Callout'
import { ConfirmDialog } from '@/console/ui/ConfirmDialog'
import { DataTable } from '@/console/ui/DataTable'
import { SectionHeading } from '@/console/ui/SectionHeading'
import { Stack } from '@/console/ui/Stack'
import { GlobalRollbackFlow } from './GlobalRollbackFlow'
import { OutcomeCounts, RESULT_COLUMNS, ReasonSummary, isMutedResult, resultCells } from './GlobalRollbackResultCells'
import { RollbackJudgement, isSelectableForRollback } from './globalRollback'
import styles from './GlobalRollback.module.css'

const SELECT_COLUMN = { key: 'select', label: '선택' }

const JudgementTable = ({ flow, judgement }: { flow: GlobalRollbackFlow; judgement: RollbackJudgement }): JSX.Element => {
  const selectionLocked = !flow.judgementCurrent || flow.requestInFlight
  const isSelected = (documentNumber: string): boolean => flow.selection.documentNumbers.includes(documentNumber)
  const selectCell = (documentNumber: string): JSX.Element => (
    <input
      type="checkbox"
      aria-label={`${documentNumber} 선택`}
      checked={isSelected(documentNumber)}
      disabled={selectionLocked}
      onChange={() => flow.selection.toggle(documentNumber)}
    />
  )

  return (
    <DataTable
      maxHeight={480}
      stickyHeader
      columns={[SELECT_COLUMN, ...RESULT_COLUMNS]}
      rows={judgement.results.map((item) => ({
        key: item.inboundDocumentNumber,
        muted: isMutedResult(item),
        selected: isSelected(item.inboundDocumentNumber),
        cells: { ...resultCells(item), select: isSelectableForRollback(item, judgement) ? selectCell(item.inboundDocumentNumber) : null },
      }))}
    />
  )
}

const ExecutionDialogBody = ({ request }: { request: GlobalInboundRollbackRequest }): JSX.Element => (
  <>
    <p style={{ margin: 0 }}>
      {request.inboundDocumentNumbers.length}건을 되돌려요 — mWMS 입고취소와 MOMS 지시·내역·집계·원장 복원이 함께 일어나요.
    </p>
    <ul className={styles.dialogList}>
      {request.mwmsAlreadyCanceledDocumentNumbers.length > 0 ? (
        <li>
          {request.mwmsAlreadyCanceledDocumentNumbers.length}건은 mWMS 취소를 보내지 않아요. mWMS 에 ASN 이 살아 있으면 재확정 때 ASN 이 중복
          생성돼요.
        </li>
      ) : null}
      <li>실행 뒤에는 아래 재확정을 눌러야 해요. 빠뜨리면 주문이 입고요청 상태로 남아요.</li>
    </ul>
  </>
)

/** 판정 결과. 판정 뒤 입력이 바뀌면 선택을 잠그고 다시 판정하라고 알린다(§6.1). */
export const GlobalRollbackJudgement = ({ flow }: { flow: GlobalRollbackFlow }): JSX.Element | null => {
  const { judgement } = flow
  if (!judgement) {
    return null
  }

  return (
    <Stack>
      <SectionHeading level={3} title="판정 결과" count={`${judgement.results.length}건`} />
      <OutcomeCounts results={judgement.results} />
      <ReasonSummary results={judgement.results} />
      {flow.judgementCurrent ? null : <Callout tone="warn">판정 뒤 입력이 바뀌었어요. 다시 판정해야 실행할 수 있어요.</Callout>}
      <JudgementTable flow={flow} judgement={judgement} />
    </Stack>
  )
}

/** 롤백 실행. 대상은 최신 판정에서 고른 `ROLLBACKABLE` 문서뿐이라 판정 전에는 누를 수 없다. */
export const GlobalRollbackExecuteAction = ({ flow }: { flow: GlobalRollbackFlow }): JSX.Element => {
  const [pendingConfirm, setPendingConfirm] = useState(false)
  const confirmExecution = (): void => {
    setPendingConfirm(false)
    flow.execute()
  }

  return (
    <div>
      <Button disabled={!flow.canExecute} onClick={() => setPendingConfirm(true)}>
        롤백 실행 ({flow.executionRequest.inboundDocumentNumbers.length}건)
      </Button>
      <ConfirmDialog
        open={pendingConfirm}
        title="롤백을 실행할까요?"
        body={<ExecutionDialogBody request={flow.executionRequest} />}
        confirmLabel="롤백 실행"
        danger
        onConfirm={confirmExecution}
        onCancel={() => setPendingConfirm(false)}
      />
    </div>
  )
}
